import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from "rxjs";
import {DjangoApiService} from "../../../shared/services/django-api.service";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {LanguageService} from "../../../shared/services/language.service";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {ActivatedRoute, Router} from "@angular/router";
import {CardsBasicComponent} from "../../../shared/components/cards/cards-basic/cards-basic.component";
import {FormsModule} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {TableEditorComponent} from "../../../shared/components/tables/table-editor/table-editor.component";

interface TableData {
  headers: string[];
  rows: string[][];
}


@Component({
  selector: 'app-data-source-detail',
  standalone: true,
  imports: [
    CardsBasicComponent,
    FormsModule,
    MatButton,
    MatCheckbox,
    MatFormField,
    MatInput,
    MatLabel,
    TranslateModule,
    TableEditorComponent
  ],
  templateUrl: './data-source-detail.component.html',
  styleUrl: './data-source-detail.component.css'
})
export class DataSourceDetailComponent implements OnInit, OnDestroy {
  productId!: number;
  dataSourceId!: number;
  dataSourceData!: any;
  copiedData!: any;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private _apiService: DjangoApiService,
    private _snackbarService: SnackbarService,
    public _languageService: LanguageService,
    private _translate: TranslateService,
    private _route: ActivatedRoute,
    private _router: Router) {
  }

  ngOnInit() {
    this.setupLanguageSubscription();
    this.getRouteParams();
  }

  getRouteParams() {
    this.subscriptions.add(
      this._route.params.subscribe(params => {
        this.productId = params['productId'];
        this.dataSourceId = params['dataSourceId'];
      })
    );

    if (!isNaN(Number(this.dataSourceId))) {
      this.loadDataSourceData();
    } else {
      this.createEmptyDataSource();
    }

  }


  createEmptyDataSource() {
    const translations: { [key: string]: { title: string, data_json: any } } = {};

    this._languageService.supportedLangs.forEach(lang => {
      translations[lang] = {
        title: '',
        data_json: ''
      };
    });

    this.dataSourceData = {
      product: Number(this.productId),
      translations: translations
    };
  }


  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }


  private loadDataSourceData() {
    this.subscriptions.add(
      this._apiService.fetchDataSource(this.dataSourceId).subscribe({
        next: (response: any) => {
          this.dataSourceData = response;
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }


  private createDataSource() {
    this.subscriptions.add(
      this._apiService.createDataSource(this.dataSourceData).subscribe({
        next: (response: any) => {
          this.dataSourceData = response.data;
          this._snackbarService.show(response.message);
          this.updateUrl();
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  private updateDataSource() {
    this.subscriptions.add(
      this._apiService.updateDataSource(this.dataSourceData).subscribe({
        next: (response: any) => {
          this.dataSourceData = response.data;
          this._snackbarService.show(response.message);
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  saveDataSource() {
    if (this.dataSourceData && this.dataSourceData.translations) {
      Object.keys(this.dataSourceData.translations).forEach(lang => {
        if (this.dataSourceData.translations[lang] && this.dataSourceData.translations[lang].data_json) {
          this.dataSourceData.translations[lang].data_json.rows = this.filterEmptyRows(this.dataSourceData.translations[lang].data_json.rows);
        }
      });
    }

    this.dataSourceData.id ? this.updateDataSource() : this.createDataSource();
  }


  private filterEmptyRows(rows: string[][]): string[][] {
    return rows.filter((row: string[]) => {
      return row.some(cell => cell !== '' && cell !== null && cell !== undefined);
    });
  }


  cancel() {
    this.dataSourceData.id ? this.loadDataSourceData() : this.createEmptyDataSource();
  }

  private updateUrl(): void {
    this._router.navigateByUrl(this._router.url.replace(String(this.dataSourceId), String(this.dataSourceData.id)));
    this.dataSourceId = this.dataSourceData.id;
  }

  outputChange(data: any, lang: string){
    this.dataSourceData.translations[lang].data_json = data;
  }

  copyData(data: any){
    this.copiedData = data;
    this._snackbarService.show('Data Copied');
  }

  pasteData(lang: string){
    this.dataSourceData.translations[lang].data_json = this.copiedData;
    this._snackbarService.show('Data Inserted');
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }



}
